import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { ThemeToggle } from '../../context/ThemeContext';
import { useUnifiedAuth } from '../../context/UnifiedAuthContext';
import { ChangePasswordModal } from '../auth/ChangePasswordModal';
import { BugReportModal } from '../bug-report/BugReportModal';
import TabBar from './TabBar';
import { Logger } from '../../utils/logger';

const log = Logger.create('ViewLayout2');

/**
 * ViewLayout2 - Neues Unified Layout für alle Admin Views
 * 
 * Struktur:
 * ┌──────────────────────────────────────────────────────────────────┐
 * │ HEADER (sticky, h-16, bg-white/gray-800)                         │
 * │ ┌──────────────────────────────────────────────────────────────┐ │
 * │ │ [←] Title                                      [🔔] [⚙️] [👤] │ │
 * │ └──────────────────────────────────────────────────────────────┘ │
 * ├──────────────────────────────────────────────────────────────────┤
 * │ ACTION BAR (h-14, bg-gray-50/gray-850, border-b)                 │
 * │ ┌──────────────────────────────────────────────────────────────┐ │
 * │ │ [Tab1] [Tab2] [Tab3]           [Search...] [+ Erstellen]     │ │
 * │ └──────────────────────────────────────────────────────────────┘ │
 * ├──────────────────────────────────────────────────────────────────┤
 * │ CONTENT (flex-1, overflow-auto, p-6)                             │
 * │                                                                  │
 * │   {children}                                                     │
 * │                                                                  │
 * └──────────────────────────────────────────────────────────────────┘
 */

// Dropdown Component für Primary Action
function ActionDropdown({ action, isOpen, onToggle, onClose }) {
  if (!action.dropdown) {
    return (
      <button
        onClick={action.onClick}
        disabled={action.disabled}
        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg theme-transition disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {action.icon && <span>{action.icon}</span>}
        {action.label}
      </button>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={onToggle}
        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg theme-transition"
      >
        {action.icon && <span>{action.icon}</span>}
        {action.label}
        <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={onClose} />
          <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-20 py-1">
            {action.dropdown.map((item, index) => (
              <button
                key={index}
                onClick={() => {
                  item.onClick();
                  onClose();
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 theme-transition-fast"
              >
                {item.icon && <span>{item.icon}</span>}
                {item.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

ActionDropdown.propTypes = {
  action: PropTypes.shape({
    label: PropTypes.string.isRequired,
    icon: PropTypes.string,
    onClick: PropTypes.func,
    disabled: PropTypes.bool,
    dropdown: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      icon: PropTypes.string,
      onClick: PropTypes.func.isRequired
    }))
  }).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};

export default function ViewLayout2({
  // Header
  title,
  onBack,
  headerActions = [],
  
  // Action Bar
  tabs,
  activeTab,
  onTabChange,
  searchPlaceholder,
  onSearch,
  primaryAction,
  secondaryActions = [],
  
  // Content
  maxWidth = '7xl',
  noPadding = false,
  
  children
}) {
  const navigate = useNavigate();
  const { logout, user, userProfile } = useUnifiedAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showBugReportModal, setShowBugReportModal] = useState(false);
  
  // Get user display info
  const userEmail = user?.email || '';
  const displayName = userProfile?.displayName || userProfile?.name || userEmail.split('@')[0];

  const handleBack = () => {
    if (typeof onBack === 'function') {
      onBack();
    } else if (typeof onBack === 'string') {
      navigate(onBack);
    } else {
      navigate(-1);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    onSearch?.(value);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (err) {
      log.error('Logout error', err);
    }
  };

  // Width classes mapping
  const maxWidthClasses = {
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl',
    '5xl': 'max-w-5xl',
    '6xl': 'max-w-6xl',
    '7xl': 'max-w-7xl',
    full: 'max-w-full'
  };

  const hasActionBar = tabs?.length > 0 || searchPlaceholder || primaryAction || secondaryActions?.length > 0;

  return (
    <div className="h-screen min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col theme-transition overflow-hidden">
      {/* ========== HEADER ========== */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-30 h-16">
        <div className={`${maxWidthClasses[maxWidth]} mx-auto px-4 sm:px-6 lg:px-8 h-full`}>
          <div className="grid grid-cols-3 items-center h-full gap-4">
            {/* Left: Back + Title */}
            <div className="flex items-center gap-3 min-w-0">
              {onBack !== undefined && (
                <button
                  onClick={handleBack}
                  className="flex-shrink-0 p-2 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg theme-transition-fast"
                  title="Zurück"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
              <h1 className="text-xl font-bold text-gray-900 dark:text-white truncate">
                {title}
              </h1>
            </div>

            {/* Center: Made with Love by BIL Hamburg */}
            <div className="flex items-center justify-center">
              <a
                href="https://www.bil-hamburg.de"
                target="_blank"
                rel="noopener noreferrer"
                className="hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 theme-transition-fast group"
                title="Made with ❤️ by BIL Hamburg"
              >
                <span className="text-xs text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300">
                  Made with
                </span>
                <span className="text-red-500">❤️</span>
                <span className="text-xs text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300">
                  by
                </span>
                <img
                  src="/LogoDruckColorOnBrightUncoatedA0.png"
                  alt="BIL Hamburg"
                  className="h-8 dark:hidden"
                />
                <img
                  src="/LogoDruckColorOnDarkUncoatedA0.png"
                  alt="BIL Hamburg"
                  className="h-8 hidden dark:block"
                />
              </a>
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-2 justify-end">{/* Custom Header Actions */}
              {headerActions.map((action, index) => (
                <button
                  key={index}
                  onClick={action.onClick}
                  className="relative p-2 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg theme-transition-fast"
                  title={action.label}
                >
                  <span className="text-lg">{action.icon}</span>
                  {action.badge && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-danger-500 text-white text-xs rounded-full flex items-center justify-center">
                      {action.badge}
                    </span>
                  )}
                </button>
              ))}
              
              {/* Bug Report Button - Kompakt mit Icon */}
              <button
                onClick={() => setShowBugReportModal(true)}
                className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg theme-transition-fast"
                title="Fehler melden"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
              
              {/* Theme Toggle - Als Button */}
              <ThemeToggle />
              
              {/* User Menu Dropdown */}
              {user && (
                <div className="relative">
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-2 px-3 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg theme-transition-fast"
                    title="Benutzermenü"
                  >
                    <span className="text-sm font-medium truncate max-w-[150px]">
                      {displayName}
                    </span>
                    <svg 
                      className={`w-4 h-4 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Dropdown Menu */}
                  {userMenuOpen && (
                    <>
                      {/* Backdrop to close on outside click */}
                      <div 
                        className="fixed inset-0 z-40" 
                        onClick={() => setUserMenuOpen(false)}
                      />
                      
                      <div className="absolute right-0 top-full mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50 overflow-hidden">
                        {/* User Info */}
                        <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                          <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                            {displayName}
                          </p>
                          <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                            {userEmail}
                          </p>
                        </div>

                        {/* Menu Items */}
                        <div className="py-2">
                          {/* Change Password */}
                          <button
                            onClick={() => {
                              setUserMenuOpen(false);
                              setShowChangePasswordModal(true);
                            }}
                            className="w-full px-4 py-2.5 text-left flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-700 theme-transition-fast"
                          >
                            <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                            </svg>
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                Passwort ändern
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                Account-Sicherheit verwalten
                              </p>
                            </div>
                          </button>

                          {/* Divider */}
                          <div className="my-2 border-t border-gray-200 dark:border-gray-700" />

                          {/* Logout */}
                          <button
                            onClick={() => {
                              setUserMenuOpen(false);
                              handleLogout();
                            }}
                            className="w-full px-4 py-2.5 text-left flex items-center gap-3 hover:bg-red-50 dark:hover:bg-red-900/20 theme-transition-fast text-red-600 dark:text-red-400"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                            <div>
                              <p className="text-sm font-medium">
                                Abmelden
                              </p>
                              <p className="text-xs opacity-75">
                                Sitzung beenden
                              </p>
                            </div>
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
              
              {/* Custom Header Actions */}
              {headerActions.map((action, index) => (
                <button
                  key={index}
                  onClick={action.onClick}
                  className="relative p-2 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg theme-transition-fast"
                  title={action.label}
                >
                  <span className="text-lg">{action.icon}</span>
                  {action.badge && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-danger-500 text-white text-xs rounded-full flex items-center justify-center">
                      {action.badge}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      </header>

      {/* Change Password Modal */}
      <ChangePasswordModal 
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
      />

      {/* Bug Report Modal */}
      <BugReportModal
        isOpen={showBugReportModal}
        onClose={() => setShowBugReportModal(false)}
      />

      {/* ========== ACTION BAR ========== */}
      {hasActionBar && (
        <div className="bg-gray-100 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 h-14">
          <div className={`${maxWidthClasses[maxWidth]} mx-auto px-4 sm:px-6 lg:px-8 h-full`}>
            <div className="flex items-center justify-between h-full gap-4">
              {/* Left: Tabs */}
              <div className="flex-1 overflow-x-auto">
                {tabs && tabs.length > 0 && (
                  <TabBar
                    tabs={tabs}
                    activeTab={activeTab}
                    onTabChange={onTabChange}
                    variant="underline"
                  />
                )}
              </div>

              {/* Right: Search + Actions */}
              <div className="flex items-center gap-3 flex-shrink-0">
                {/* Search */}
                {searchPlaceholder && (
                  <div className="relative">
                    <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={handleSearch}
                      placeholder={searchPlaceholder}
                      className="w-48 pl-9 pr-4 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent theme-transition"
                    />
                  </div>
                )}

                {/* Secondary Actions */}
                {secondaryActions.map((action, index) => (
                  <button
                    key={index}
                    onClick={action.onClick}
                    disabled={action.disabled}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg theme-transition-fast disabled:opacity-50"
                  >
                    {action.icon && <span>{action.icon}</span>}
                    {action.label}
                  </button>
                ))}

                {/* Primary Action */}
                {primaryAction && (
                  <ActionDropdown
                    action={primaryAction}
                    isOpen={dropdownOpen}
                    onToggle={() => setDropdownOpen(!dropdownOpen)}
                    onClose={() => setDropdownOpen(false)}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========== CONTENT ========== */}
      <main className="flex-1 overflow-y-auto">
        <div className={`${maxWidthClasses[maxWidth]} mx-auto ${noPadding ? '' : 'p-6'}`}>
          <div className="space-y-6">
            {children}
          </div>
        </div>
      </main>
    </div>
  );
}

ViewLayout2.propTypes = {
  // Header
  title: PropTypes.string.isRequired,
  onBack: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  headerActions: PropTypes.arrayOf(PropTypes.shape({
    icon: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    badge: PropTypes.number
  })),
  
  // Action Bar
  tabs: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    icon: PropTypes.string,
    count: PropTypes.number
  })),
  activeTab: PropTypes.string,
  onTabChange: PropTypes.func,
  searchPlaceholder: PropTypes.string,
  onSearch: PropTypes.func,
  primaryAction: PropTypes.shape({
    label: PropTypes.string.isRequired,
    icon: PropTypes.string,
    onClick: PropTypes.func,
    disabled: PropTypes.bool,
    dropdown: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      icon: PropTypes.string,
      onClick: PropTypes.func.isRequired
    }))
  }),
  secondaryActions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    icon: PropTypes.string,
    onClick: PropTypes.func.isRequired,
    disabled: PropTypes.bool
  })),
  
  // Content
  maxWidth: PropTypes.oneOf(['md', 'lg', 'xl', '2xl', '3xl', '4xl', '5xl', '6xl', '7xl', 'full']),
  noPadding: PropTypes.bool,
  
  children: PropTypes.node.isRequired
};
